import { useState, useEffect, useCallback } from 'react';
import { Shield, Download, Search, Filter } from 'lucide-react';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_TYPES = [
  'all', 'user_signup', 'user_login', 'login_failed', 'consent_signed',
  'feedback_submitted', 'account_deleted', 'admin_login', 'support_agent_login',
  'rate_limit_triggered', 'ticket_created', 'broadcast_sent', 'user_suspended', 'user_unsuspended'
];

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const fetchLogs = useCallback(async () => {
    const params = new URLSearchParams({ action_type: filter, search, skip: page * 50, limit: 50 });
    try {
      const res = await fetch(`${API}/api/admin/audit?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (err) { logger.error("Operation failed:", err); }
  }, [filter, search, page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const exportCSV = () => {
    const headers = 'Timestamp,Action,User ID,IP,Result,Details\n';
    const rows = logs.map(l => `${l.timestamp},${l.action_type},${l.user_id || ''},${l.ip_address},${l.result},"${l.details || ''}"`).join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'sendpi_audit.csv'; a.click();
  };

  const resultColor = { success: 'text-green-400', failure: 'text-red-400' };

  return (
    <div data-testid="admin-audit" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Audit Log</h1>
        <button onClick={exportCSV} className="px-3 py-1.5 rounded-lg text-xs bg-white/5 text-white/40 hover:text-white flex items-center gap-1">
          <Download className="w-3 h-3" /> Export
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="Search by user ID or IP..."
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50"
          />
        </div>
        <select
          value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
        >
          {ACTION_TYPES.map(t => <option key={t} value={t} className="bg-[#0E1830]">{t === 'all' ? 'All Actions' : t.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div className="bg-[#0E1830] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-3 text-white/40 font-medium">Time</th>
              <th className="text-left px-4 py-3 text-white/40 font-medium">Action</th>
              <th className="text-left px-4 py-3 text-white/40 font-medium">User</th>
              <th className="text-left px-4 py-3 text-white/40 font-medium">IP</th>
              <th className="text-left px-4 py-3 text-white/40 font-medium">Result</th>
              <th className="text-left px-4 py-3 text-white/40 font-medium">Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={`${l.timestamp}-${l.action_type}-${l.ip_address}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-2.5 text-white/50 text-xs whitespace-nowrap">{l.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                <td className="px-4 py-2.5 text-white text-xs">{l.action_type?.replace(/_/g, ' ')}</td>
                <td className="px-4 py-2.5 text-white/50 text-xs font-mono">{l.user_id?.slice(-6) || l.admin_id?.slice(-6) || '-'}</td>
                <td className="px-4 py-2.5 text-white/50 text-xs font-mono">{l.ip_address}</td>
                <td className={`px-4 py-2.5 text-xs ${resultColor[l.result] || 'text-white/40'}`}>{l.result}</td>
                <td className="px-4 py-2.5 text-white/30 text-xs max-w-[200px] truncate">{l.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-white/40">
        <span>{total} total entries</span>
        <div className="flex gap-2">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3 py-1 bg-white/5 rounded disabled:opacity-30">Prev</button>
          <span className="px-3 py-1">Page {page + 1}</span>
          <button onClick={() => setPage(page + 1)} disabled={logs.length < 50} className="px-3 py-1 bg-white/5 rounded disabled:opacity-30">Next</button>
        </div>
      </div>
    </div>
  );
}
